import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../utils/authFetch";

const euro = (value) =>
  new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
  }).format(Number(value || 0));

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("it-IT") : "—";

export default function AppAgenteProvvigioniPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const filteredOrders = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("it-IT");

    return orders.filter((order) => {
      const company = String(order.azienda || "").toLocaleLowerCase("it-IT");
      const number = String(order.order_id || "");
      const date = String(order.date || "").slice(0, 10);

      if (term && !company.includes(term) && !number.includes(term)) return false;
      if (dateFrom && (!date || date < dateFrom)) return false;
      if (dateTo && (!date || date > dateTo)) return false;

      return true;
    });
  }, [orders, search, dateFrom, dateTo]);

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        const response = await authFetch("/api/agente/provvigioni/");
        if (!response.ok) {
          throw new Error("Impossibile caricare le provvigioni.");
        }
        const data = await response.json();
        if (active) setOrders(Array.isArray(data.orders) ? data.orders : []);
      } catch (err) {
        if (active) setError(err.message || "Errore di caricamento.");
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => { active = false; };
  }, []);

  const totals = useMemo(() => {
    return orders.reduce(
      (acc, order) => {
        acc.fatturato += Number(order.subtotal || 0);
        acc.provvigioni += Number(order.commission_amount || 0);
        acc.liquidato += Number(order.paid_amount || 0);
        acc.residuo += Number(order.liquidatable_amount || 0);
        acc.sospese += Math.max(
          Number(order.remaining_amount || 0) -
          Number(order.liquidatable_amount || 0),
          0
        );
        return acc;
      },
      { fatturato: 0, provvigioni: 0, liquidato: 0, residuo: 0, sospese: 0 }
    );
  }, [orders]);

  return (
    <div className="container py-4" style={{ maxWidth: 1000 }}>
      <button
        type="button"
        className="btn btn-outline-secondary mb-4"
        onClick={() => navigate("/app")}
      >
        ← Torna alla Home
      </button>

      <h2 className="fw-bold mb-2">Vendite e provvigioni</h2>
      <p className="text-muted mb-4">
        Riepilogo delle provvigioni già attribuite al tuo account.
      </p>

      {loading && <p>Caricamento dati...</p>}
      {error && <div className="alert alert-danger">{error}</div>}

      {!loading && !error && (
        <>
          <div className="row g-3 mb-4">
            {[
              ["Fatturato collegato", totals.fatturato],
              ["Provvigioni attribuite", totals.provvigioni],
              ["Già liquidato", totals.liquidato],
              ["Da liquidare", totals.residuo],
              ["In sospeso", totals.sospese],
            ].map(([label, value]) => (
              <div className="col-6 col-lg-3" key={label}>
                <div className="card h-100 shadow-sm">
                  <div className="card-body">
                    <div className="text-muted small">{label}</div>
                    <div className="fs-5 fw-bold">{euro(value)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <h5 className="fw-bold mb-3">Dettaglio ordini</h5>

          <div className="row g-2 mb-3">
            <div className="col-12 col-md-6">
              <label className="form-label" htmlFor="agent-search">
                Azienda o numero ordine
              </label>
              <input
                id="agent-search"
                type="search"
                className="form-control"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Cerca un ordine..."
              />
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label" htmlFor="agent-from">Dal</label>
              <input
                id="agent-from"
                type="date"
                className="form-control"
                value={dateFrom}
                onChange={(event) => setDateFrom(event.target.value)}
              />
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label" htmlFor="agent-to">Al</label>
              <input
                id="agent-to"
                type="date"
                className="form-control"
                value={dateTo}
                onChange={(event) => setDateTo(event.target.value)}
              />
            </div>
          </div>

          {orders.length > 0 && filteredOrders.length === 0 && (
            <div className="alert alert-light border">
              Nessun ordine corrisponde ai filtri selezionati.
            </div>
          )}

          {orders.length === 0 && (
            <div className="alert alert-info">
              Non risultano ancora provvigioni attribuite al tuo account.
            </div>
          )}

          {filteredOrders.map((order) => (
            <div className="card shadow-sm mb-3" key={order.order_id}>
              <div className="card-body">
                <div className="d-flex justify-content-between gap-3 flex-wrap">
                  <div>
                    <div className="fw-bold">
                      Ordine #{order.order_id} — {order.azienda || "Azienda non indicata"}
                    </div>
                    <div className="text-muted small">
                      {formatDate(order.date)}
                      {" · "}
                      Pagamento azienda:{" "}
                      <strong>
                        {order.company_is_paid ? "Pagato" : "Da incassare"}
                      </strong>
                      {" · "}
                      Metodo:{" "}
                      {order.payment_method === "ONLINE"
                        ? "Online"
                        : order.payment_method === "CONSEGNA"
                        ? "Alla consegna"
                        : order.payment_method || "Non indicato"}
                      <div className="mt-1">
                        Provvigione:{" "}
                        <strong>
                          {({
                            DA_DETERMINARE: "Da determinare",
                            SOSPESA: "Sospesa",
                            MATURATA: "Maturata",
                            PARZIALMENTE_LIQUIDATA: "Parzialmente liquidata",
                            LIQUIDATA: "Liquidata",
                          })[order.commission_display_status] ||
                            order.commission_status}
                        </strong>
                      </div>
                    </div>
                  </div>
                  <div className="text-end">
                    <div className="fw-bold">
                      {order.commission_amount == null
                        ? "Da determinare"
                        : euro(order.commission_amount)}
                    </div>
                    <div className="small text-muted">
                      {order.commission_percent == null
                        ? "Percentuale da verificare"
                        : `${order.commission_percent}% · Liquidabile ${euro(order.liquidatable_amount)} · Sospeso ${euro(
                            Math.max(
                              Number(order.remaining_amount || 0) -
                              Number(order.liquidatable_amount || 0),
                              0
                            )
                          )}`}
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm mt-3 me-2"
                  onClick={() =>
                    navigate(`/app/agente/ordini/${encodeURIComponent(order.order_id)}/dettaglio`)
                  }
                >
                  Visualizza ordine
                </button>

                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm mt-3"
                  onClick={() =>
                    setExpandedId(expandedId === order.order_id ? null : order.order_id)
                  }
                >
                  {expandedId === order.order_id ? "Chiudi dettaglio" : "Vedi pagamenti"}
                </button>

                {expandedId === order.order_id && (
                  <div className="mt-3 border-top pt-3">
                    <p className="mb-2">
                      Netto ordine: <strong>{euro(order.subtotal)}</strong>
                    </p>
                    <p className="mb-2">
                      Liquidato: <strong>{euro(order.paid_amount)}</strong>
                    </p>
                    {(order.payments || []).length === 0 ? (
                      <p className="text-muted mb-0">Nessun pagamento registrato.</p>
                    ) : (
                      <ul className="mb-0">
                        {order.payments.map((payment) => (
                          <li key={payment.id}>
                            {formatDate(payment.paid_at)} — {euro(payment.amount)}
                            {payment.payment_method ? ` (${payment.payment_method})` : ""}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
